import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  CardContent,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    const result = await login({ email, password });
    
    if (result.success) {
      navigate('/organization-selection'); // Redirect to organization selection after successful login
    } else {
      setLocalError(result.message || 'Login failed');
    }
  };

  const displayError = localError || error;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => theme.palette.primary.main,
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              //background: (theme) => theme.palette.primary.dark,
              background: 'white',
              color: '000000',
              textAlign: 'center',
              py: 4,
              px: 3,
            }}
          >
            <img 
              src={process.env.PUBLIC_URL + '/Aneo-logo-primary-black_RGB.svg'} 
              alt="Aneo Logo" 
              style={{ 
                width: '80px', 
                height: '80px'
              }} 
            />
            <Typography variant="h2" component="h1" gutterBottom >
              Welcome to Powersite
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Please log in to continue
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={isLoading}
                autoComplete="email"
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={isLoading}
                autoComplete="current-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              {displayError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {displayError}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: (theme) => theme.palette.secondary.dark + 'BF', // 75% opacity,
                  '&:hover': {
                    background: (theme) => theme.palette.secondary.dark,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
